/**
 * NARROWING HELPERS ONLY. `ApiError` lives in the handed `client.ts`; there is no second error
 * class and nothing here has a constructor.
 */
import { ApiError, type Problem } from "./client";

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

export function problemOf(e: unknown): Problem | null {
  return isApiError(e) ? e.problem : null;
}

function typeEndsWith(e: unknown, suffix: string): boolean {
  const problem = problemOf(e);
  return !!problem && problem.type.endsWith(suffix);
}

/** 409: a concurrent write, or two run creates in the same instant. */
export function isConflict(e: unknown): boolean {
  return problemOf(e)?.status === 409;
}

/**
 * 404 of the ordinary kind: the row is gone, or the tenant has no succeeded run. NOT the operator
 * 404 and NOT either derived-output 404 — those three carry their own `type` and their own
 * rendering, and lumping them in here is exactly the mistake IS-D15 exists to prevent.
 */
export function isNotFound(e: unknown): boolean {
  return (
    problemOf(e)?.status === 404 &&
    !isTenantNotResolved(e) &&
    !isDerivedOutputPending(e) &&
    !isDerivedOutputUnavailable(e)
  );
}

/** 404 of the operator kind: the hostname is not a tenant. */
export function isTenantNotResolved(e: unknown): boolean {
  return problemOf(e)?.status === 404 && typeEndsWith(e, "/tenant-not-resolved");
}

/**
 * DERIVED OUTPUT, AND THE THREE WAYS IT CAN BE ABSENT.
 *
 * A run's findings are computed by a queued task AFTER the run succeeds, so a succeeded run can
 * be asked for output that does not exist yet. The API says which kind of nothing it is with an
 * RFC 9457 `type` on a 404 — not a new status, because `API-CONTRACT.md` §6 already makes 404 on
 * a run-scoped collection "the one status that is not an error" and `ReportFrame` already renders
 * it as an empty state (IS-D15, and the analytics design's AN-D11, verbatim the same two URIs).
 *
 * A READY RESULT WITH NOTHING TO REPORT IS A 200 with `{items: [], total: 0}` and means a
 * genuinely clean plan. It must never be rendered the same way as either of these.
 */

/** 404 `derived-output-pending`: still being computed. Retrying WILL help. */
export function isDerivedOutputPending(e: unknown): boolean {
  return problemOf(e)?.status === 404 && typeEndsWith(e, "/derived-output-pending");
}

/** 404 `derived-output-unavailable`: it failed, or the run predates the feature. Retrying will not help. */
export function isDerivedOutputUnavailable(e: unknown): boolean {
  return problemOf(e)?.status === 404 && typeEndsWith(e, "/derived-output-unavailable");
}

/**
 * How long to wait before asking again for a pending derived output.
 *
 * The server sends `Retry-After: 5` beside the problem. The handed `client.ts` surfaces the
 * problem BODY and not the response headers, and that file is generated territory, so the
 * published interval is written down here once rather than read off a header the client throws
 * away. It is part of the same contract as the two URIs above.
 */
export const DERIVED_OUTPUT_RETRY_MS = 5_000;

/** 422: returns the per-field failures, or `null` when this is not a validation problem. */
export function isValidation(e: unknown): Problem["errors"] | null {
  const problem = problemOf(e);
  if (!problem || problem.status !== 422) return null;
  return problem.errors ?? [];
}

/** 415: the upload file is neither CSV nor XLSX by content. */
export function isUnsupportedUploadFormat(e: unknown): boolean {
  return problemOf(e)?.status === 415 && typeEndsWith(e, "/unsupported-upload-format");
}
